import { useActiveVkuiLocation, useRouteNavigator } from '@vkontakte/vk-mini-apps-router';
import type { NavIdProps } from '@vkontakte/vkui';
import { useState } from 'react';
import { api } from '../../api/client';
import { usePagedLoader } from '../../api/paging';
import { useSession } from '../../api/session';
import type { Page } from '../../api/types';
import { PANEL } from '../../app/router';
import { t } from '../../i18n';
import { LinkIcon, PlusIcon } from '../../shared/icons';
import { useSearchTerm } from '../../shared/lib/search';
import {
  Action,
  CardHeading,
  CardLink,
  CardText,
  EmptyState,
  ErrorState,
  Gap,
  Gutter,
  LoadingState,
  Muted,
  Notice,
  Screen,
  SearchField,
  Section,
  Thumb,
  TileGrid,
  toast,
} from '../../shared/ui';
import { canEditFaceClaims, FaceClaimEdit, type FaceClaimItem } from './FaceClaimEdit';

/**
 * Сколько заметки влезает в плитку. Обрезаем в коде, а не многоточием на две
 * строки: у заметки две тысячи знаков, и без предела соседние плитки в ряду
 * вытягиваются по самой длинной.
 */
const NOTE_PREVIEW = 90;

/**
 * База внешек: самостоятельный справочник, перенос вики-страницы сообщества.
 * С анкетами он не связан — здесь нет ни проверок «внешка занята», ни броней.
 */
export function FaceClaimsPanel({ id }: NavIdProps) {
  const session = useSession();
  const routeNavigator = useRouteNavigator();
  const canEdit = canEditFaceClaims(session);

  const search = useSearchTerm('faces.search');
  const [creating, setCreating] = useState(false);

  // Уходящая панель ещё живёт на время анимации — грузить в ней нечего.
  const { panel } = useActiveVkuiLocation();
  const isActive = panel === PANEL.FACES_LIST;

  const list = usePagedLoader<FaceClaimItem>(
    (params, signal) =>
      api.get<Page<FaceClaimItem>>(
        '/api/face-claims',
        { search: search.term || undefined, limit: params.limit, offset: params.offset },
        signal,
      ),
    [search.term],
    isActive,
    'faces',
  );

  // Как в реестре: выдача прошлого поиска под новым читалась бы как несработавший фильтр.
  const page = list.leftover ? null : list.data;
  const items = page?.items ?? [];
  const total = page?.total ?? 0;
  /* Первая страница и дочитывание — разные состояния экрана, но у загрузчика
     флаг один: пока списка нет, это первое чтение, дальше — хвост. */
  const initial = page === null && list.loading;
  // Дочитывание идёт — кнопка остаётся на месте со спиннером, а не исчезает.
  const waiting =
    list.loadMore === null && list.loading && page !== null && items.length < page.total;

  if (creating) {
    return (
      <Screen id={id} title={t('face.screen.create')} back={() => setCreating(false)}>
        <FaceClaimEdit
          initial={null}
          canUploadPhoto={session.user?.canWrite === true}
          onCancel={() => setCreating(false)}
          onSaved={(card) => {
            setCreating(false);
            list.reload();
            toast(t('face.saved.created', { name: card.name }));
          }}
        />
      </Screen>
    );
  }

  return (
    <Screen id={id} title={t('face.title')} count={page === null ? null : total} unit="face">
      <Section>
        {/* Поиск и «завести» стоят одной строкой: карточку заводит модератор, и
            занимать под редкую кнопку полосу во всю ширину над справочником
            незачем. */}
        <SearchField
          value={search.input}
          onChange={search.setInput}
          placeholder={t('face.search.placeholder')}
          action={
            canEdit ? (
              <Action
                tone="action"
                size="m"
                before={<PlusIcon />}
                onClick={() => setCreating(true)}
              >
                {t('face.action.create')}
              </Action>
            ) : null
          }
        />
      </Section>

      <Section>
        {initial ? <LoadingState text={t('face.loading.list')} /> : null}

        {list.error !== null && items.length === 0 ? (
          <ErrorState message={list.error} onRetry={list.reload} />
        ) : null}

        {page !== null && list.error === null && items.length === 0 ? (
          <EmptyState
            title={search.term ? t('face.empty.notFound') : t('face.empty.list')}
            action={
              search.term ? (
                <Action tone="quiet" size="m" onClick={() => search.setInput('')}>
                  {t('face.search.clear')}
                </Action>
              ) : null
            }
          >
            {search.term
              ? t('face.empty.notFoundHint')
              : canEdit
                ? t('face.empty.listHintEditor')
                : t('face.empty.listHint')}
          </EmptyState>
        ) : null}

        {items.length > 0 ? (
          <TileGrid>
            {items.map((item) => (
              <FaceTile
                key={item.id}
                item={item}
                onOpen={() => routeNavigator.push(`/faces/${item.id}`)}
              />
            ))}
          </TileGrid>
        ) : null}

        {list.loadMore !== null || waiting ? (
          <Gutter>
            <Action
              tone="quiet"
              size="l"
              stretched
              loading={waiting}
              onClick={list.loadMore ?? undefined}
            >
              {t('common.action.showMore')}
            </Action>
          </Gutter>
        ) : null}

        {/* Не пришло дочитывание или перечитывание в фоне после возврата, а
            показанное на месте — поэтому строка под списком, а не заглушка на
            весь экран. Пока есть что дочитать, повтор — это «показать ещё»;
            когда нечего, у строки своя кнопка, иначе ошибка висела бы без выхода. */}
        {list.error !== null && items.length > 0 ? (
          <Gutter>
            <Notice
              tone="error"
              action={
                list.loadMore === null ? (
                  <Action tone="quiet" size="s" onClick={list.reload}>
                    {t('common.action.retry')}
                  </Action>
                ) : undefined
              }
            >
              {list.error}
            </Notice>
          </Gutter>
        ) : null}
      </Section>
    </Screen>
  );
}

/**
 * Плитка справочника: фото во всю колонку, под ним имя, метка ссылки и начало
 * заметки.
 *
 * Аватар на 56 px оставлял от лица кружок в углу строки, а фото — единственное,
 * ради чего в справочник заходят.
 *
 * Просмотр на весь экран живёт в карточке: кнопка Thumb внутри кнопки
 * CardLink — это вложенные кнопки, разметка запрещает их, а Tab и читалка с
 * экрана на них спотыкаются.
 */
function FaceTile({ item, onOpen }: { item: FaceClaimItem; onOpen: () => void }) {
  return (
    <CardLink
      onClick={onOpen}
      label={t('face.action.openCard', { name: item.name })}
      tone="published"
      padded={false}
    >
      <Thumb image={item.image} fill ratio="portrait" alt={item.name} />
      <CardText>
        {/* Значок вместо адреса: он длинный, а плитке важно лишь, есть ли куда
            посмотреть. Подписью «ссылка» это занимало треть строки имени. */}
        <CardHeading
          badge={
            item.link ? (
              <LinkIcon size={12} tone="accent" label={t('face.card.hasLink')} />
            ) : undefined
          }
        >
          {item.name}
        </CardHeading>
        {item.note ? (
          <>
            <Gap step="s1h" />
            <Muted>{notePreview(item.note)}</Muted>
          </>
        ) : null}
      </CardText>
    </CardLink>
  );
}

function notePreview(note: string): string {
  return note.length <= NOTE_PREVIEW ? note : `${note.slice(0, NOTE_PREVIEW).trimEnd()}…`;
}
